import { useState } from "react";
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Link, MenuItem, Stack, Switch, Tab, Table, TableBody, TableCell, TableHead,
  TablePagination, TableRow, Tabs, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/AppLayout";
import { dismissAlert, fetchAlerts, fetchAlertStats, undismissAlert } from "@/api/alerts";
import type { Alert, AlertStats, AlertStatus, ApprovalType } from "@/types";

const TYPE_LABELS: Record<ApprovalType, string> = {
  permit: "Permit",
  interconnection: "Interconnection",
  pto: "PTO",
  architectural: "Architectural",
};

const PER_PAGE = 50;

function fmtDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

export function AlertsDashboardPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<AlertStatus>("active");
  const [approvalType, setApprovalType] = useState<ApprovalType | "">("");
  const [actionableOnly, setActionableOnly] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(0); // MUI is 0-indexed
  const [dismissTarget, setDismissTarget] = useState<Alert | null>(null);
  const [reason, setReason] = useState("");

  const stats = useQuery({ queryKey: ["alert-stats"], queryFn: fetchAlertStats });

  const filters = {
    status,
    approval_type: approvalType || undefined,
    actionable: actionableOnly || undefined,
    q: q.trim() || undefined,
    page: page + 1,
    per_page: PER_PAGE,
  };
  const alerts = useQuery({ queryKey: ["alerts", filters], queryFn: () => fetchAlerts(filters) });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["alerts"] });
    qc.invalidateQueries({ queryKey: ["alert-stats"] });
  };

  const dismiss = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => dismissAlert(id, reason),
    onSuccess: () => {
      setDismissTarget(null);
      setReason("");
      invalidate();
    },
  });

  const undismiss = useMutation({
    mutationFn: (id: number) => undismissAlert(id),
    onSuccess: invalidate,
  });

  const changeStatus = (next: AlertStatus) => {
    setStatus(next);
    setPage(0);
  };

  const tabLabel = (label: string, count: number | undefined) =>
    count === undefined ? label : `${label} (${count})`;

  const s = stats.data as AlertStats | undefined;

  return (
    <AppLayout>
      <Typography variant="h5" gutterBottom>Approvals Not Yet in Sunrise</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Approvals detected in AHJ/utility portals or the approvals@ / CAM inboxes whose Sunrise upload task is
        still open. Dismiss the ones you&apos;ve handled — a dismissed alert stays hidden until the underlying
        approval changes.
      </Typography>

      <Tabs value={status} onChange={(_e, v) => changeStatus(v)} sx={{ mb: 2 }}>
        <Tab value="active" label={tabLabel("Active", s?.active)} />
        <Tab value="dismissed" label={tabLabel("Dismissed", s?.dismissed)} />
        <Tab value="resolved" label={tabLabel("Resolved", s?.resolved)} />
      </Tabs>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 2 }} alignItems="center">
        <TextField
          select size="small" label="Type" value={approvalType} sx={{ minWidth: 160 }}
          onChange={(e) => { setApprovalType(e.target.value as ApprovalType | ""); setPage(0); }}
        >
          <MenuItem value="">All types</MenuItem>
          {(Object.keys(TYPE_LABELS) as ApprovalType[]).map((t) => (
            <MenuItem key={t} value={t}>{TYPE_LABELS[t]}</MenuItem>
          ))}
        </TextField>
        <TextField
          size="small" label="Search project / address / status" value={q} sx={{ minWidth: 280 }}
          onChange={(e) => { setQ(e.target.value); setPage(0); }}
        />
        {status === "active" && (
          <FormControlLabel
            control={<Switch checked={actionableOnly} onChange={(e) => { setActionableOnly(e.target.checked); setPage(0); }} />}
            label="Actionable only"
          />
        )}
      </Stack>

      {alerts.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
      ) : (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Project</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Address / Subject</TableCell>
                <TableCell>Status / Summary</TableCell>
                <TableCell>Pending Task</TableCell>
                <TableCell>Detected</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {alerts.data?.items.map((a) => (
                <TableRow key={a.id} hover>
                  <TableCell>
                    {a.project_url
                      ? <Link href={a.project_url} target="_blank" rel="noreferrer">{a.project_id}</Link>
                      : <em>(unmatched)</em>}
                  </TableCell>
                  <TableCell>{TYPE_LABELS[a.approval_type] ?? a.approval_type}</TableCell>
                  <TableCell>{a.source}</TableCell>
                  <TableCell>{a.address ?? "—"}</TableCell>
                  <TableCell>{a.detail ?? "—"}</TableCell>
                  <TableCell>
                    {a.pending_task
                      ? a.pending_task
                      : <Chip size="small" label="None pending" variant="outlined" />}
                  </TableCell>
                  <TableCell>{fmtDate(a.detected_at)}</TableCell>
                  <TableCell align="right">
                    {a.status === "dismissed" ? (
                      <Button size="small" disabled={undismiss.isPending} onClick={() => undismiss.mutate(a.id)}>
                        Undismiss
                      </Button>
                    ) : a.status === "active" ? (
                      <Button size="small" color="error" onClick={() => setDismissTarget(a)}>
                        Dismiss
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {alerts.data?.items.length === 0 && (
                <TableRow><TableCell colSpan={8}><em>No alerts.</em></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={alerts.data?.total ?? 0}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={PER_PAGE}
            rowsPerPageOptions={[PER_PAGE]}
          />
        </>
      )}

      <Dialog open={dismissTarget !== null} onClose={() => setDismissTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Dismiss alert</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dismissTarget?.project_id} · {dismissTarget && TYPE_LABELS[dismissTarget.approval_type]}
          </Typography>
          <TextField
            autoFocus fullWidth multiline minRows={2} label="Reason (optional)"
            value={reason} onChange={(e) => setReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissTarget(null)}>Cancel</Button>
          <Button
            variant="contained" color="error" disabled={dismiss.isPending}
            onClick={() => dismissTarget && dismiss.mutate({ id: dismissTarget.id, reason: reason.trim() })}
          >
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </AppLayout>
  );
}
